import { useState, useEffect } from 'react';

interface FeatureFlag {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  category: 'feature' | 'experiment' | 'debug';
  defaultValue: boolean;
  overridden: boolean;
}

// Default feature flags
const DEFAULT_FLAGS: Omit<FeatureFlag, 'enabled' | 'overridden'>[] = [
  // Features
  { id: 'ENABLE_POLLING', name: 'Polling Data', description: 'Show polling data and charts', category: 'feature', defaultValue: false },
  { id: 'ENABLE_MARKETS', name: 'Market Odds', description: 'Show prediction market odds', category: 'feature', defaultValue: false },
  { id: 'ENABLE_ELECTIONS_MAP', name: 'Elections Map', description: 'Interactive map with Mapbox', category: 'feature', defaultValue: false },
  { id: 'ENABLE_DASHBOARD', name: 'User Dashboard', description: 'Personalized user dashboard', category: 'feature', defaultValue: false },
  { id: 'ENABLE_WATCHLIST', name: 'Watchlist', description: 'Track favorite candidates/races', category: 'feature', defaultValue: true },
  { id: 'ENABLE_ALERTS', name: 'Alerts', description: 'Real-time notifications', category: 'feature', defaultValue: false },
  { id: 'ENABLE_STRIPE', name: 'Stripe Payments', description: 'Premium subscriptions', category: 'feature', defaultValue: false },
  
  // Experiments
  { id: 'EXP_NEW_NAV', name: 'New Navigation', description: 'Test new navigation layout', category: 'experiment', defaultValue: false },
  { id: 'EXP_DARK_THEME', name: 'Dark Theme', description: 'Dark color scheme', category: 'experiment', defaultValue: true },
  { id: 'EXP_COMPACT_CARDS', name: 'Compact Cards', description: 'Smaller candidate cards', category: 'experiment', defaultValue: false },
  
  // Debug
  { id: 'DEBUG_SHOW_IDS', name: 'Show IDs', description: 'Display entity IDs in UI', category: 'debug', defaultValue: false },
  { id: 'DEBUG_API_LOGS', name: 'API Logging', description: 'Log all API calls to console', category: 'debug', defaultValue: false },
  { id: 'DEBUG_DEMO_DATA', name: 'Demo Data', description: 'Use mock data instead of API', category: 'debug', defaultValue: false },
  { id: 'DEBUG_SLOW_MODE', name: 'Slow Mode', description: 'Add artificial delays', category: 'debug', defaultValue: false },
];

const STORAGE_KEY = 'republic-atlas-feature-flags';

export const FeatureFlags = () => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [filter, setFilter] = useState<'all' | 'feature' | 'experiment' | 'debug'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Load flags from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const overrides: Record<string, boolean> = stored ? JSON.parse(stored) : {};
    
    const loadedFlags = DEFAULT_FLAGS.map(flag => ({
      ...flag,
      enabled: overrides[flag.id] !== undefined ? overrides[flag.id] : flag.defaultValue,
      overridden: overrides[flag.id] !== undefined,
    }));
    
    setFlags(loadedFlags);
  }, []);

  // Save flag change
  const toggleFlag = (flagId: string) => {
    setFlags(prev => {
      const updated = prev.map(flag => {
        if (flag.id === flagId) {
          return { ...flag, enabled: !flag.enabled, overridden: true };
        }
        return flag;
      });
      
      // Save to localStorage
      const overrides: Record<string, boolean> = {};
      updated.forEach(f => {
        if (f.overridden) {
          overrides[f.id] = f.enabled;
        }
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
      
      return updated;
    });
  };

  // Reset a specific flag
  const resetFlag = (flagId: string) => {
    setFlags(prev => {
      const updated = prev.map(flag => {
        if (flag.id === flagId) {
          const defaultFlag = DEFAULT_FLAGS.find(f => f.id === flagId);
          return { ...flag, enabled: defaultFlag?.defaultValue || false, overridden: false };
        }
        return flag;
      });
      
      // Update localStorage
      const overrides: Record<string, boolean> = {};
      updated.forEach(f => {
        if (f.overridden) {
          overrides[f.id] = f.enabled;
        }
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
      
      return updated;
    });
  };

  // Reset all flags
  const resetAll = () => {
    localStorage.removeItem(STORAGE_KEY);
    setFlags(DEFAULT_FLAGS.map(flag => ({
      ...flag,
      enabled: flag.defaultValue,
      overridden: false,
    })));
  };

  const filteredFlags = flags.filter(flag => {
    if (filter !== 'all' && flag.category !== filter) return false;
    if (searchTerm && !flag.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: flags.length,
    enabled: flags.filter(f => f.enabled).length,
    overridden: flags.filter(f => f.overridden).length,
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'feature': return '🚀';
      case 'experiment': return '🧪';
      case 'debug': return '🔧';
      default: return '📌';
    }
  };

  return (
    <div className="space-y-4">
      {/* Natural Language Summary */}
      <div className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 rounded-lg p-3 border border-purple-500/30">
        <p className="text-sm">
          <span className="text-purple-400 font-medium">Feature Flags:</span>{' '}
          <span className="text-gray-200">
            {stats.enabled} of {stats.total} flags enabled
            {stats.overridden > 0 && (
              <>, <span className="text-yellow-400">{stats.overridden} overridden</span></>
            )}
          </span>
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Search flags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
        />
        <select 
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs"
        >
          <option value="all">All</option>
          <option value="feature">Features</option>
          <option value="experiment">Experiments</option>
          <option value="debug">Debug</option>
        </select>
        <button
          onClick={resetAll}
          className="bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded text-xs"
        >
          Reset All
        </button>
      </div>

      {/* Flags List */}
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {filteredFlags.map((flag) => (
          <div
            key={flag.id}
            className={`bg-gray-800 rounded-lg p-3 ${flag.overridden ? 'border border-yellow-500/50' : ''}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span>{getCategoryIcon(flag.category)}</span>
                  <span className="text-sm font-medium text-gray-200">{flag.name}</span>
                  {flag.overridden && (
                    <span className="text-[10px] bg-yellow-600/30 text-yellow-400 px-1 rounded">OVERRIDDEN</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">{flag.description}</p>
                <code className="text-[10px] text-gray-600 font-mono">{flag.id}</code>
              </div>
              <div className="flex items-center gap-2">
                {flag.overridden && (
                  <button
                    onClick={() => resetFlag(flag.id)}
                    className="text-xs text-gray-500 hover:text-gray-300"
                    title="Reset to default"
                  >
                    ↺
                  </button>
                )}
                <button
                  onClick={() => toggleFlag(flag.id)}
                  className={`w-12 h-6 rounded-full transition-colors ${
                    flag.enabled ? 'bg-green-600' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                    flag.enabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Export/Import */}
      <div className="flex gap-2">
        <button
          onClick={() => {
            const data = JSON.stringify(flags.reduce((acc, f) => ({ ...acc, [f.id]: f.enabled }), {}), null, 2);
            navigator.clipboard.writeText(data);
            alert('Flags copied to clipboard!');
          }}
          className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded text-xs"
        >
          📋 Copy Config
        </button>
        <button
          onClick={() => {
            const data = prompt('Paste flag config JSON:');
            if (data) {
              try {
                const parsed = JSON.parse(data);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
                window.location.reload();
              } catch {
                alert('Invalid JSON');
              }
            }
          }}
          className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded text-xs"
        >
          📥 Import Config
        </button>
      </div>

      {/* Usage hint */}
      <div className="text-[10px] text-gray-600 bg-gray-800/50 rounded p-2">
        <strong>Usage in code:</strong>
        <pre className="mt-1">
{`import { useFeatureFlag } from '@/hooks/useFeatureFlag';
const isEnabled = useFeatureFlag('ENABLE_POLLING');`}
        </pre>
      </div>
    </div>
  );
};

// Hook for using feature flags in components
export const useFeatureFlag = (flagId: string): boolean => {
  const [enabled, setEnabled] = useState(false);
  
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const overrides: Record<string, boolean> = stored ? JSON.parse(stored) : {};
    const defaultFlag = DEFAULT_FLAGS.find(f => f.id === flagId);
    setEnabled(overrides[flagId] !== undefined ? overrides[flagId] : defaultFlag?.defaultValue || false);
  }, [flagId]);
  
  return enabled;
};

